import React, { useState } from "react";
import { Editor } from "@tiptap/react";
import { IconButton, Menu, MenuItem, Popover, Tooltip } from "@mui/material";
import FormatBold from "@mui/icons-material/FormatBold";
import FormatItalic from "@mui/icons-material/FormatItalic";
import FormatUnderlined from "@mui/icons-material/FormatUnderlined";
import FormatSize from "@mui/icons-material/FormatSize";
import FormatColorText from "@mui/icons-material/FormatColorText";
import { ChromePicker, ColorResult } from "react-color";

import * as S from "./styles";

const FONT_SIZE_OPTIONS = [
	{ label: "Large", value: "30px" },
	{ label: "Medium", value: "20px" },
	{ label: "Small", value: "14px" },
];
const DEFAULT_FONT_SIZE = "14px";

interface IToolbarProps {
	editor: Editor | null;
	primaryColor?: string;
}

function Toolbar({ editor, primaryColor }: IToolbarProps) {
	const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
	const [colorAnchor, setColorAnchor] = useState<null | HTMLElement>(null);

	if (!editor) return null;

	const hasPrimaryColor = Boolean(primaryColor);

	const currentFontSize = (editor.getAttributes("textStyle").fontSize as string | undefined) ?? DEFAULT_FONT_SIZE;

	const currentTextColor = (editor.getAttributes("textStyle").color as string | undefined) ?? null;

	const isPrimaryColorActive = hasPrimaryColor && editor.isActive("textStyle", { color: primaryColor });

	function handleOpenMenu(event: React.MouseEvent<HTMLElement>) {
		setMenuAnchor(event.currentTarget);
	}

	function handleCloseMenu() {
		setMenuAnchor(null);
	}

	function handleFontSizeSelect(value: string) {
		editor?.chain().focus().setFontSize(value).run();
		handleCloseMenu();
	}

	function handleTogglePrimaryColor() {
		if (!primaryColor) return;
		if (isPrimaryColorActive) {
			editor?.chain().focus().unsetTextColor().run();
		} else {
			editor?.chain().focus().setTextColor(primaryColor).run();
		}
	}

	function handleOpenColorPicker(event: React.MouseEvent<HTMLElement>) {
		setColorAnchor(event.currentTarget);
	}

	function handleCloseColorPicker() {
		setColorAnchor(null);
	}

	function handleColorChange(value: ColorResult) {
		editor?.chain().focus().setTextColor(value.hex).run();
	}

	function renderPrimaryColorButton() {
		return (
			<Tooltip title={`Default color (${primaryColor})`}>
				<IconButton
					size="small"
					color={isPrimaryColorActive ? "primary" : "default"}
					onClick={handleTogglePrimaryColor}
				>
					<FormatColorText fontSize="small" sx={{ color: primaryColor }} />
				</IconButton>
			</Tooltip>
		);
	}

	function renderFreeColorPicker() {
		return (
			<>
				<Tooltip title="Text color">
					<IconButton size="small" color={colorAnchor ? "primary" : "default"} onClick={handleOpenColorPicker}>
						<FormatColorText fontSize="small" sx={currentTextColor ? { color: currentTextColor } : undefined} />
					</IconButton>
				</Tooltip>
				<Popover
					anchorEl={colorAnchor}
					open={Boolean(colorAnchor)}
					onClose={handleCloseColorPicker}
					anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
					transformOrigin={{ vertical: "top", horizontal: "left" }}
				>
					<S.ColorPickerWrapper>
						<ChromePicker color={currentTextColor ?? "#000000"} onChange={handleColorChange} disableAlpha />
					</S.ColorPickerWrapper>
				</Popover>
			</>
		);
	}

	return (
		<S.Toolbar>
			<IconButton
				size="small"
				color={editor.isActive("bold") ? "primary" : "default"}
				onClick={() => editor.chain().focus().toggleBold().run()}
			>
				<FormatBold fontSize="small" />
			</IconButton>
			<IconButton
				size="small"
				color={editor.isActive("italic") ? "primary" : "default"}
				onClick={() => editor.chain().focus().toggleItalic().run()}
			>
				<FormatItalic fontSize="small" />
			</IconButton>
			<IconButton
				size="small"
				color={editor.isActive("underline") ? "primary" : "default"}
				onClick={() => editor.chain().focus().toggleUnderline().run()}
			>
				<FormatUnderlined fontSize="small" />
			</IconButton>

			<IconButton size="medium" color={menuAnchor ? "primary" : "default"} onClick={handleOpenMenu}>
				<FormatSize fontSize="small" />
			</IconButton>
			<Menu
				anchorEl={menuAnchor}
				open={Boolean(menuAnchor)}
				onClose={handleCloseMenu}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				{FONT_SIZE_OPTIONS.map(({ label, value }) => (
					<MenuItem
						key={value}
						selected={currentFontSize === value}
						onClick={() => handleFontSizeSelect(value)}
						sx={{ fontSize: "0.8125rem" }}
					>
						{label}
					</MenuItem>
				))}
			</Menu>

			{hasPrimaryColor ? renderPrimaryColorButton() : renderFreeColorPicker()}
		</S.Toolbar>
	);
}

export default Toolbar;
